import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Fade,
} from '@mui/material';
import { Analytics, CheckCircle } from '@mui/icons-material';
import { API_URL } from './config';
import axios from 'axios';

const PROGRESS_STEPS = [
  { label: 'Creating Short Summary', duration: 6000 },
  { label: 'Creating Detailed Summary', duration: 10000 },
  { label: 'Analyzing Writing style and tone', duration: 8000 },
  { label: 'Analyzing Key narrative and thematic elements', duration: 6000 },
  { label: 'Additional Analysis', duration: 6000 },
];

function AnalysisModal({ open, onClose, video }) {
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const [scriptAnalysis, setScriptAnalysis] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [analysisJobId, setAnalysisJobId] = useState(null);
  const stepTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Progress animation effect
  useEffect(() => {
    if (analysisLoading) {
      setCurrentStep(0);

      let stepIndex = 0;
      setCurrentStep(stepIndex);

      const advanceStep = () => {
        stepIndex++;
        if (stepIndex < PROGRESS_STEPS.length) {
          setCurrentStep(stepIndex);
          // Schedule next step after current step's duration
          stepTimeoutRef.current = setTimeout(advanceStep, PROGRESS_STEPS[stepIndex].duration);
        }
        // Last step will continue until loading finishes
      };

      // Start advancing after first step's duration
      if (PROGRESS_STEPS.length > 0 && stepIndex < PROGRESS_STEPS.length - 1) {
        stepTimeoutRef.current = setTimeout(advanceStep, PROGRESS_STEPS[stepIndex].duration);
      }

      return () => {
        if (stepTimeoutRef.current) {
          clearTimeout(stepTimeoutRef.current);
        }
      };
    } else {
      // Reset when loading stops
      setCurrentStep(0);
      if (stepTimeoutRef.current) {
        clearTimeout(stepTimeoutRef.current);
      }
    }
  }, [analysisLoading]);

  // Poll job status when a job is active
  useEffect(() => {
    if (!analysisJobId || !analysisLoading) {
      return;
    }

    const pollJobStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/jobs/${analysisJobId}`);
        const job = response.data;

        // Check if job is complete
        if (job.status === 'completed') {
          setAnalysisLoading(false);
          setAnalysisJobId(null);

          // Fetch the analysis from the video
          try {
            const analysisResponse = await axios.get(
              `${API_URL}/api/videos/${video.videoId}/script-analysis`,
            );
            setScriptAnalysis(analysisResponse.data.scriptAnalysis);
          } catch (error) {
            console.error('Error fetching analysis:', error);
            setAnalysisError(error.message || 'Failed to load analysis');
          }
        } else if (job.status === 'failed') {
          setAnalysisLoading(false);
          setAnalysisJobId(null);
          const errorMessage =
            job.errors && job.errors.length > 0
              ? job.errors[0].error || 'Analysis failed'
              : 'Analysis failed';
          setAnalysisError(errorMessage);
        }
        // If still processing, continue polling
      } catch (error) {
        console.error('Error polling job status:', error);
        // Don't stop polling on transient errors
      }
    };

    // Poll immediately, then every 2 seconds
    pollJobStatus();
    pollingIntervalRef.current = setInterval(pollJobStatus, 2000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [analysisJobId, analysisLoading, video]);

  // Reset state when modal opens/closes or video changes
  useEffect(() => {
    if (open && video) {
      setAnalysisError('');

      // If scriptAnalysis already exists in video prop, use it
      if (video.scriptAnalysis && Object.keys(video.scriptAnalysis).length > 0) {
        setScriptAnalysis(video.scriptAnalysis);
        return;
      }

      // Check if video has already been analyzed
      const hasDoneAnalysis =
        video.scriptAnalysis &&
        video.scriptAnalysis.shortSummary &&
        video.scriptAnalysis.detailedSummary &&
        video.scriptAnalysis.pointOfView &&
        video.scriptAnalysis.writingStyle &&
        video.scriptAnalysis.tone &&
        video.scriptAnalysis.features;

      if (hasDoneAnalysis) {
        // Try to fetch it from the API to ensure we have the latest
        setAnalysisLoading(true);
        const fetchAnalysis = async () => {
          try {
            const response = await axios.get(`${API_URL}/api/videos/${video.videoId}/script-analysis`);
            setScriptAnalysis(response.data.scriptAnalysis);
          } catch (error) {
            console.error('Error fetching analysis:', error);
            setAnalysisError(error.message || 'Failed to load analysis');
          } finally {
            setAnalysisLoading(false);
          }
        };
        fetchAnalysis();
        return;
      }

      // Otherwise, create a job to analyze the video
      setAnalysisLoading(true);
      const createAnalysisJob = async () => {
        try {
          const response = await axios.post(`${API_URL}/api/videos/${video.videoId}/analyze`);
          setAnalysisJobId(response.data.jobId);
        } catch (error) {
          console.error('Error creating analysis job:', error);
          setAnalysisError(error.message || 'Failed to start analysis');
          setAnalysisLoading(false);
        }
      };

      createAnalysisJob();
    } else if (!open) {
      // Reset state when modal closes (but keep scriptAnalysis for potential reuse)
      setAnalysisError('');
      setCurrentStep(0);
      setAnalysisJobId(null);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    }
  }, [open, video]);

  const handleClose = () => {
    onClose();
  };

  if (!video) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      onClick={(e) => {
        e.stopPropagation();
      }}
      PaperProps={{
        sx: {
          background:
            'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
          border: '1px solid rgba(148, 163, 184, 0.1)',
          borderRadius: 3,
        },
        onClick: (e) => {
          e.stopPropagation();
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Analytics />
          <Typography variant="h6">Script Analysis</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {analysisLoading ? (
          <Box sx={{ py: 4 }}>
            {PROGRESS_STEPS.map((step, index) => {
              const isCompleted = index < currentStep;
              const isCurrent = index === currentStep;
              const isUpcoming = index > currentStep;

              return (
                <Fade
                  key={index}
                  in={isCompleted || isCurrent}
                  timeout={500}
                  style={{ transitionDelay: isCompleted ? '0ms' : '200ms' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                      opacity: isUpcoming ? 0.3 : 1,
                      transition: 'opacity 0.3s ease-in-out',
                    }}
                  >
                    <Box sx={{ minWidth: 40, display: 'flex', justifyContent: 'center' }}>
                      {isCompleted ? (
                        <CheckCircle
                          sx={{
                            color: 'success.main',
                            fontSize: 28,
                            animation: 'scaleIn 0.3s ease-out',
                            '@keyframes scaleIn': {
                              '0%': { transform: 'scale(0)', opacity: 0 },
                              '100%': { transform: 'scale(1)', opacity: 1 },
                            },
                          }}
                        />
                      ) : isCurrent ? (
                        <CircularProgress
                          size={28}
                          thickness={4}
                          sx={{
                            color: 'primary.main',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: 'text.disabled',
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: isCompleted
                          ? 'success.main'
                          : isCurrent
                            ? 'primary.main'
                            : 'text.disabled',
                        fontWeight: isCurrent ? 600 : 400,
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                </Fade>
              );
            })}
          </Box>
        ) : analysisError ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {analysisError}
          </Alert>
        ) : scriptAnalysis ? (
          <Box sx={{ mt: 1 }}>
            {/* Short Summary */}
            {scriptAnalysis.shortSummary && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}
                >
                  Short Summary
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}
                >
                  {scriptAnalysis.shortSummary}
                </Typography>
              </Box>
            )}

            {/* Point of View */}
            {scriptAnalysis.pointOfView && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5, color: 'primary.main' }}
                >
                  Point of View
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {scriptAnalysis.pointOfView}
                </Typography>
              </Box>
            )}

            {/* Detailed Summary */}
            {scriptAnalysis.detailedSummary && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}
                >
                  Detailed Summary
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}
                >
                  {scriptAnalysis.detailedSummary}
                </Typography>
              </Box>
            )}

            {/* Writing Style */}
            {scriptAnalysis.writingStyle && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5, color: 'primary.main' }}
                >
                  Writing Style
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {scriptAnalysis.writingStyle}
                </Typography>
              </Box>
            )}

            {/* Tone */}
            {scriptAnalysis.tone && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 0.5, color: 'primary.main' }}
                >
                  Tone
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {scriptAnalysis.tone}
                </Typography>
              </Box>
            )}

            {/* Features */}
            {scriptAnalysis.features && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}
                >
                  Features
                </Typography>
                {scriptAnalysis.features.centralConflict && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Central Conflict:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      {scriptAnalysis.features.centralConflict}
                    </Typography>
                  </Box>
                )}
                {scriptAnalysis.features.protagonistAntagonists && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Protagonist/Antagonists:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      {scriptAnalysis.features.protagonistAntagonists}
                    </Typography>
                  </Box>
                )}
                {scriptAnalysis.features.moralComplexity && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Moral Complexity:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      {scriptAnalysis.features.moralComplexity}
                    </Typography>
                  </Box>
                )}
                {scriptAnalysis.features.plotTwist && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Plot Twist:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      {scriptAnalysis.features.plotTwist}
                    </Typography>
                  </Box>
                )}
                {scriptAnalysis.features.historicalSetting && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Historical Setting:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                      {scriptAnalysis.features.historicalSetting}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No analysis available
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AnalysisModal;
